import React from 'react';
import { Box, Typography, Card, CardContent, Chip } from '@mui/material';
import { useThemeContext } from '../../../hook/useThemeManager';
import { theme } from '../../../constants/theme';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { ICompanyDetails } from '../../../redux/company/types';
import { isEmpty } from '../../../utils/lodash';
import { formatCurrency } from '../../../constants/currency';
import { convertMinuteSecondsDays } from '../../../utils/AppUtils';
import CustomButton from '../../Button/CustomButton';
import Pagination from '../../Common/Pagination';
import { useDispatch } from 'react-redux';
import { getCompanyDetails } from '../../../redux/company/actions';
import { useParams } from 'react-router-dom';
import { navigate } from '../../../redux/navigator/action';
import { replaceRouteParam } from '../../../utils/routesProcessor';
import { IRole } from '../../../types/global/helper';
import { ROUTES } from '../../../types/global/routes.types';
const niceColors = require('nice-color-palettes');
const colorsNice = [...niceColors[67], ...niceColors[1], ...niceColors[29], ...niceColors[71], ...niceColors[81]];
interface IJobsTab {
    companyDetails?: ICompanyDetails;
}

const JobsTab: React.FC<IJobsTab> = ({ companyDetails }) => {
    const { themeStyle } = useThemeContext();
    const dispatch = useDispatch();
    const params = useParams();
    const { id } = params;
    const { data, pagination } = companyDetails?.jobs! || {};

    const handlePageChange = (page: number) => {
        if (id) {
            dispatch(
                getCompanyDetails({
                    slug: id,
                    jobsPage: page,
                    jobsLimit: pagination?.pageSize || 10,
                    withJobs: true,
                    singleFetch: true,
                }),
            );
        }
    };

    const handleViewDetails = (role: IRole) => {
        const route = replaceRouteParam(ROUTES.AUDITION_DETAIL, 'auditionId', role.audition.slug!);
        dispatch(
            navigate({
                routes: route,
            }),
        );
    };

    if (!data || isEmpty(data)) {
        return (
            <Box
                sx={{
                    textAlign: 'center',
                    py: 4,
                    color: theme[themeStyle].tag_white,
                }}>
                <Typography
                    sx={{
                        fontFamily: FONT_ENUM.MEDIUM_TEXT,
                        fontSize: '1rem',
                    }}>
                    No open positions available at the moment.
                </Typography>
            </Box>
        );
    }

    return (
        <Box>
            <Typography
                variant="h6"
                sx={{
                    color: theme[themeStyle].tag_white,
                    fontFamily: FONT_ENUM.BOLD_HEADER,
                    mb: 3,
                }}>
                Open Positions ({pagination.totalCount})
            </Typography>

            <Box sx={{ spaceY: 2 }}>
                {data &&
                    !isEmpty(data) &&
                    data.map((job, index) => (
                        <Card
                            key={index}
                            sx={{
                                backgroundColor: theme[themeStyle].pure_black,
                                border: `1px solid ${theme[themeStyle].tag_white_light}`,
                                borderRadius: 2,
                                mb: 2,
                                cursor: 'pointer',
                                '&:hover': {
                                    backgroundColor: theme[themeStyle].prisma_dark,
                                },
                            }}>
                            <CardContent sx={{ p: 3 }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        justifyContent: 'space-between',
                                        mb: 2,
                                    }}>
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            color: theme[themeStyle].tag_white,
                                            fontFamily: FONT_ENUM.BOLD_HEADER,
                                        }}>
                                        {job.character}
                                    </Typography>
                                    <Chip
                                        label={job.employment_type}
                                        size="small"
                                        sx={{
                                            backgroundColor: colorsNice[0],
                                            color: theme[themeStyle].black,
                                            fontFamily: FONT_ENUM.MEDIUM_TEXT,
                                            textTransform: 'capitalize',
                                        }}
                                    />
                                </Box>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 2,
                                        mb: 2,
                                        flexWrap: 'wrap',
                                    }}>
                                    <Typography
                                        sx={{
                                            color: theme[themeStyle].tag_white,
                                            fontFamily: FONT_ENUM.MEDIUM_TEXT,
                                            fontSize: '0.875rem',
                                        }}>
                                        {job?.audition?.location?.address || 'TBD'}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            color: theme[themeStyle].tag_white,
                                            fontFamily: FONT_ENUM.MEDIUM_TEXT,
                                            fontSize: '0.875rem',
                                        }}>
                                        •
                                    </Typography>
                                    {job.compensation && (
                                        <Typography
                                            sx={{
                                                color: theme[themeStyle].tag_white,
                                                fontFamily: FONT_ENUM.MEDIUM_TEXT,
                                                fontSize: '0.875rem',
                                            }}>
                                            {formatCurrency(Number(job.compensation), job.currency)}
                                        </Typography>
                                    )}
                                    <Typography
                                        sx={{
                                            color: theme[themeStyle].tag_white,
                                            fontFamily: FONT_ENUM.MEDIUM_TEXT,
                                            fontSize: '0.875rem',
                                        }}>
                                        •
                                    </Typography>
                                    <Typography
                                        sx={{
                                            color: theme[themeStyle].tag_white,
                                            fontFamily: FONT_ENUM.MEDIUM_TEXT,
                                            fontSize: '0.875rem',
                                        }}>
                                        {convertMinuteSecondsDays(job.createdAt)} ago
                                    </Typography>
                                </Box>
                                <Typography
                                    sx={{
                                        color: theme[themeStyle].tag_white,
                                        fontFamily: FONT_ENUM.MEDIUM_TEXT,
                                        fontSize: '0.875rem',
                                        mb: 2,
                                    }}>
                                    {job.description}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                                    {job.required_media.map((required, skillIndex) => (
                                        <Chip
                                            key={skillIndex}
                                            label={required}
                                            size="small"
                                            variant="outlined"
                                            sx={{
                                                bgcolor: colorsNice[8],
                                                color: theme[themeStyle].black,
                                                fontFamily: FONT_ENUM.MEDIUM_TEXT,
                                                fontSize: '0.75rem',
                                                textTransform: 'capitalize',
                                            }}
                                        />
                                    ))}
                                </Box>

                                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                    <CustomButton
                                        label="View Details"
                                        onClick={() => handleViewDetails(job)}
                                        sx={{
                                            width: '100%',
                                            height: '48px',
                                            fontSize: '0.875rem',
                                            fontFamily: FONT_ENUM.MEDIUM_TEXT,
                                        }}
                                    />
                                </Box>
                            </CardContent>
                        </Card>
                    ))}
            </Box>

            {/* Pagination */}
            {pagination && (pagination.hasNextPage || pagination.hasPrevPage) && (
                <Pagination
                    variant="company"
                    currentPage={pagination.currentPage || 1}
                    totalPages={pagination.totalPages || 1}
                    onPageChange={page => {
                        handlePageChange(page);
                    }}
                    hasNextPage={pagination.hasNextPage}
                    hasPreviousPage={pagination.hasPrevPage}
                />
            )}
        </Box>
    );
};

export { JobsTab };
